import { useEffect, useMemo, useRef, useState } from "react";
import { ZxcvbnFactory } from "@zxcvbn-ts/core";
import * as zxcvbnCommon from "@zxcvbn-ts/language-common";
import * as zxcvbnEn from "@zxcvbn-ts/language-en";
import { Eye, EyeOff, Copy, Check, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

// Initialize zxcvbn (v4 factory API).
const zxcvbnInstance = new ZxcvbnFactory({
  translations: zxcvbnEn.translations,
  graphs: zxcvbnCommon.adjacencyGraphs,
  dictionary: {
    ...zxcvbnCommon.dictionary,
    ...zxcvbnEn.dictionary,
  },
});

const STRENGTH_LABEL = {
  0: "Çok zayıf",
  1: "Çok zayıf",
  2: "Zayıf",
  3: "İyi",
  4: "Güçlü",
};

const STRENGTH_COLOR = {
  0: "text-red-600 bg-red-500",
  1: "text-red-600 bg-red-500",
  2: "text-amber-600 bg-amber-500",
  3: "text-blue-600 bg-blue-500",
  4: "text-emerald-600 bg-emerald-500",
};

// Safe symbol set excludes ambiguous & shell-troublesome characters.
const GEN_UPPER = "ABCDEFGHJKLMNPQRSTUVWXYZ"; // no I, O
const GEN_LOWER = "abcdefghjkmnpqrstuvwxyz"; // no l, o
const GEN_DIGIT = "23456789";                 // no 0, 1
const GEN_SYMBOL = "!@#$%^&*+=-_?";
const GEN_ALL = GEN_UPPER + GEN_LOWER + GEN_DIGIT + GEN_SYMBOL;

/**
 * Generate a cryptographically-strong password with guaranteed complexity.
 * Uses window.crypto.getRandomValues (Web Crypto API); never leaves the browser.
 */
function generateSecurePassword(length = 16) {
  const buf = new Uint32Array(length);
  window.crypto.getRandomValues(buf);
  // Guarantee one from each class.
  const guaranteed = [
    GEN_UPPER[buf[0] % GEN_UPPER.length],
    GEN_LOWER[buf[1] % GEN_LOWER.length],
    GEN_DIGIT[buf[2] % GEN_DIGIT.length],
    GEN_SYMBOL[buf[3] % GEN_SYMBOL.length],
  ];
  const rest = [];
  for (let i = 4; i < length; i++) {
    rest.push(GEN_ALL[buf[i] % GEN_ALL.length]);
  }
  const chars = [...guaranteed, ...rest];
  // Fisher-Yates shuffle with fresh entropy.
  const shuffle = new Uint32Array(length);
  window.crypto.getRandomValues(shuffle);
  for (let i = chars.length - 1; i > 0; i--) {
    const j = shuffle[i] % (i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join("");
}

/**
 * PasswordField — reusable password input with real strength estimation
 * (zxcvbn-ts), secure generator (Web Crypto), show/hide, copy, and Turkish
 * strength labels. Reports validity + strength to the parent.
 *
 * Props:
 *  - id, label, value, onChange, placeholder
 *  - userInputs: string[] used by zxcvbn to penalise passwords similar to
 *    the user's name/email etc.
 *  - onStrengthChange(score): callback whenever strength changes.
 *  - onGenerated(pw): callback after generator fills the field (parent can
 *    also sync a confirmation field).
 *  - minLength (default 8)
 *  - requireStrong (default true): if true, valid requires score >= 3.
 *  - testIdPrefix: base for data-testid attributes.
 */
export function PasswordField({
  id,
  label = "Şifre",
  value,
  onChange,
  onStrengthChange,
  onGenerated,
  placeholder = "En az 8 karakter",
  userInputs = [],
  minLength = 8,
  requireStrong = true,
  testIdPrefix = "password",
  showGenerator = true,
  autoComplete = "new-password",
}) {
  const [show, setShow] = useState(false);
  const [copied, setCopied] = useState(false);
  const [generatedNote, setGeneratedNote] = useState("");
  const copyTimer = useRef(null);

  const result = useMemo(() => {
    if (!value) return { score: 0, feedback: { warning: "", suggestions: [] } };
    try {
      return zxcvbnInstance.check(value, userInputs.filter(Boolean));
    } catch {
      return { score: 0, feedback: { warning: "", suggestions: [] } };
    }
  }, [value, userInputs]);

  const score = result.score;
  const lengthOk = value.length >= minLength;
  const isValid = lengthOk && (requireStrong ? score >= 3 : true);

  useEffect(() => {
    onStrengthChange?.({ score, valid: isValid, length: value.length });
  }, [score, isValid, value.length, onStrengthChange]);

  const handleGenerate = async () => {
    const pw = generateSecurePassword(16);
    onChange(pw);
    onGenerated?.(pw);
    setShow(true); // reveal so user can see and save it before submitting

    // Auto-copy to clipboard so the user can paste it into a password manager
    // straight away. Silent fallback if the browser denies clipboard access.
    let clipboardOk = false;
    try {
      await navigator.clipboard.writeText(pw);
      clipboardOk = true;
      setCopied(true);
      clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard denied — user can copy manually */
    }

    if (clipboardOk) {
      toast.success("Şifre panoya kopyalandı");
      setGeneratedNote("Güçlü bir şifre oluşturuldu ve panoya kopyalandı. Güvenli bir yere kaydet.");
    } else {
      setGeneratedNote("Güçlü bir şifre oluşturuldu. Kopyala butonuyla al ve güvenli bir yere kaydet.");
    }
  };

  const handleCopy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setCopied(false), 1500);
      toast.success("Şifre panoya kopyalandı");
    } catch {
      toast.error("Kopyalanamadı");
    }
  };

  const helpText = (() => {
    if (value.length === 0) return `Minimum ${minLength} karakter`;
    if (!lengthOk) return `${minLength - value.length} karakter daha`;
    return STRENGTH_LABEL[score];
  })();

  const helpColor = (() => {
    if (value.length === 0) return "text-zinc-500";
    if (!lengthOk) return "text-amber-600";
    return STRENGTH_COLOR[score].split(" ")[0]; // text color only
  })();

  const barColor = STRENGTH_COLOR[score].split(" ")[1]; // bg-* only
  const barSegments = 4;

  return (
    <div className="space-y-1.5" data-testid={`${testIdPrefix}-field`}>
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor={id}>{label}</Label>
        {showGenerator && (
          <button
            type="button"
            onClick={handleGenerate}
            className="text-xs text-[#1E3A8A] hover:underline inline-flex items-center gap-1"
            data-testid={`${testIdPrefix}-generate`}
          >
            <Sparkles className="w-3.5 h-3.5" />
            Güçlü şifre oluştur
          </button>
        )}
      </div>
      <div className="relative">
        <Input
          id={id}
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          minLength={minLength}
          className="rounded-xl pr-20"
          placeholder={placeholder}
          autoComplete={autoComplete}
          spellCheck={false}
          data-testid={`${testIdPrefix}-input`}
          aria-describedby={`${id}-help`}
        />
        <div className="absolute inset-y-0 right-2 flex items-center gap-1">
          <button
            type="button"
            onClick={handleCopy}
            className="p-1.5 text-zinc-500 hover:text-zinc-900 rounded-md hover:bg-zinc-100"
            aria-label="Şifreyi kopyala"
            data-testid={`${testIdPrefix}-copy`}
            title="Kopyala"
          >
            {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
          </button>
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            className="p-1.5 text-zinc-500 hover:text-zinc-900 rounded-md hover:bg-zinc-100"
            aria-label={show ? "Şifreyi gizle" : "Şifreyi göster"}
            aria-pressed={show}
            data-testid={`${testIdPrefix}-toggle`}
            title={show ? "Gizle" : "Göster"}
          >
            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Strength meter — 4 segments */}
      <div className="flex gap-1" aria-hidden="true">
        {Array.from({ length: barSegments }).map((_, i) => (
          <div
            key={i}
            className={`h-1 flex-1 rounded-full transition-colors ${
              value.length > 0 && i < Math.max(1, score) ? barColor : "bg-zinc-200"
            }`}
          />
        ))}
      </div>

      <div
        id={`${id}-help`}
        data-testid={`${testIdPrefix}-help`}
        className={`text-xs ${helpColor}`}
      >
        {helpText}
        {value.length >= minLength && score < 3 && result.feedback?.warning && (
          <span className="text-zinc-500"> · {translateFeedback(result.feedback.warning)}</span>
        )}
      </div>

      {generatedNote && (
        <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-md px-2 py-1.5" data-testid={`${testIdPrefix}-generated-note`}>
          {generatedNote}
        </div>
      )}
    </div>
  );
}

// zxcvbn feedback is English; translate common warnings to Turkish.
function translateFeedback(msg) {
  const map = {
    "This is a top-10 common password": "Yaygın parola",
    "This is a top-100 common password": "Yaygın parola",
    "This is a very common password": "Çok yaygın parola",
    "This is similar to a commonly used password": "Yaygın parolaya benziyor",
    "A word by itself is easy to guess": "Tek kelime kolayca tahmin edilir",
    "Names and surnames by themselves are easy to guess": "İsimler kolayca tahmin edilir",
    "Common names and surnames are easy to guess": "Yaygın isimler kolayca tahmin edilir",
    "Straight rows of keys are easy to guess": "Klavyede düz sıra kolayca tahmin edilir",
    "Short keyboard patterns are easy to guess": "Kısa klavye deseni kolayca tahmin edilir",
    "Repeats like \"aaa\" are easy to guess": "Tekrar eden karakterler kolayca tahmin edilir",
    "Repeats like \"abcabcabc\" are only slightly harder to guess than \"abc\"": "Tekrar eden desen zayıf",
    "Sequences like abc or 6543 are easy to guess": "Sıralı karakterler kolayca tahmin edilir",
    "Recent years are easy to guess": "Güncel yıllar kolayca tahmin edilir",
    "Dates are often easy to guess": "Tarihler kolayca tahmin edilir",
  };
  return map[msg] || "Zayıf desen";
}

export default PasswordField;
